import { getCertificateKind, type CertificateKind } from './certificateKind';

/**
 * User-facing copy shown when the uploaded .cer is the wrong kind for the
 * field — `fiel` when a CSD/sello was dropped into an e.firma uploader, `csd`
 * for the reverse.
 */
export const WRONG_CERT_MESSAGE: Record<'fiel' | 'csd', string> = {
	fiel: 'Este archivo parece ser un certificado de sello digital (CSD), no tu e.firma. Sube el .cer de tu e.firma.',
	csd: 'Ese es el .cer de tu e.firma; para facturar va el de tus sellos (CSD).',
};

/**
 * Whether `file` is acceptable for `expectedCertKind`. True when it matches,
 * when the bytes can't be parsed (fail open — we never block a legit
 * credential), or when no kind is expected. False only when we are certain the
 * cert is the wrong kind.
 */
export const isExpectedCertKind = async (
	file: File | undefined | null,
	expectedCertKind?: CertificateKind,
): Promise<boolean> => {
	if (!file || (expectedCertKind !== 'fiel' && expectedCertKind !== 'csd')) {
		return true;
	}
	const kind = await getCertificateKind(file);
	if (kind === 'unknown') return true;
	return kind === expectedCertKind;
};

/**
 * react-hook-form `validate` rule for a cert (.cer) upload: returns `true` when
 * the cert is acceptable, or the user-facing mismatch message otherwise. Shares
 * the detection with `CredentialUploadForm` so every uploader — modal or raw
 * linking form — rejects the wrong document the exact same way.
 */
export const certKindRule =
	(expectedCertKind: 'fiel' | 'csd') =>
	async (file: File | undefined | null): Promise<true | string> =>
		(await isExpectedCertKind(file, expectedCertKind)) ||
		WRONG_CERT_MESSAGE[expectedCertKind];
